/**
 * ================================================================
 * ORGANIZER SERVICE - Unified Service (Phase 1 Refactor)
 * ================================================================
 *
 * Handles "Organizer" Persona operations (Registration, Verification).
 * - Distinct from Organization (Club) management.
 * - Uses Strict Zod Types.
 *
 * @module OrganizerService
 */

import apiClient from "@/lib/apiClient";
import { organizers as Organizer, organizersSchema } from "@mebi/schemas";
import {
  profileService,
  UserProfile,
} from "@/features/common/services/profile.service";
import { organizationService } from "@/features/organizations/services/organization.service";
import { UploadService, getR2PublicUrl } from "@/storage/uploadService";

// ... existing imports

export interface OrganizationStatus {
  status:
    | "no_user"
    | "no_organization"
    | "pending"
    | "verified"
    | "rejected"
    | "error";
  organizerPersona: Organizer | null;
  organizationEntity: Record<string, unknown> | null;
  organization: Record<string, unknown> | null;
}

export type OrganizationType = "community" | "club" | "business" | "federation";

export interface RegisterOrganizerRequest {
  name: string; // Business/Club Name
  displayName?: string;
  type: OrganizationType;
  license_type?: string;
  contact_email?: string;
  contactEmail?: string;
  contact_phone?: string;
  contactPhone?: string;
  address?: string;
  website?: string;
  fanpageUrl?: string;
  contactPerson?: string;
  contactPosition?: string;

  // Uploads
  logo?: File;
  logo_url?: string;
  logoUrl?: string;
  verificationDocument?: File;
  verificationDocuments?: File[];
}

export type UpdateOrganizerProfileRequest = Partial<RegisterOrganizerRequest>;

export interface OrganizerApiResult<T = unknown> {
  success: boolean;
  message: string;
  data?: T;
  error?: string;
}

export const organizerService = {
  getErrorMessage(error: unknown): string {
    if (error instanceof Error) return error.message;
    if (typeof error === "string") return error;
    const err = error as {
      message?: string;
      response?: { data?: { message?: string } };
    };
    return err.response?.data?.message || err.message || "Đã có lỗi xảy ra";
  },
  // ... existing methods

  /**
   * Check if user is a verified organizer.
   * Uses Profile API (SSOT) to check the 'organizers' table status.
   */
  async isVerifiedOrganizer(): Promise<boolean> {
    try {
      try {
        const profile = await profileService.getCurrentUserProfile();
        if (profile?.organizer) {
          return profile.organizer.verified_status === "verified";
        }
      } catch (error) {
        console.warn("[OrganizerService] Profile check failed", error);
      }

      return false;
    } catch {
      return false;
    }
  },

  async canAccessOrganizerFeatures(): Promise<boolean> {
    return await this.isVerifiedOrganizer();
  },

  /**
   * Get detailed organization status.
   * Returns both Persona (Role) and Entity (Club) information.
   */
  async getVerificationStatus(): Promise<OrganizationStatus> {
    try {
      let persona: Organizer | null = null;
      let entity: Record<string, unknown> | null = null;
      let status: OrganizationStatus["status"] = "no_organization";

      // 1. Fetch Persona (Role Status)
      try {
        const profileData: UserProfile =
          await profileService.getCurrentUserProfile();
        if (profileData?.organizer) {
          persona = profileData.organizer;
          status =
            (persona.verified_status as OrganizationStatus["status"]) ||
            "pending";
        }
      } catch (error) {
        console.warn("[OrganizerService] Profile fetch failed", error);
      }

      // 2. Fetch Entity (Club Details)
      try {
        const response = await organizationService.getMyOrganizations();
        const data =
          response.success && Array.isArray(response.data) ? response.data : [];
        if (data && data.length > 0) {
          entity = data[0] as Record<string, unknown>;
        }
      } catch {
        // Ignore entity fetch failure
      }

      // 3. Construct Response
      return {
        status,
        organizerPersona: persona,
        organizationEntity: entity,
        organization:
          entity || (persona as unknown as Record<string, unknown>) || null,
      };
    } catch {
      return {
        status: "error",
        organizerPersona: null,
        organizationEntity: null,
        organization: null,
      };
    }
  },

  /**
   * Register as an Organizer (Create Persona)
   * Handles Logo Upload + Reg + Docs Upload
   */
  async register(
    data: RegisterOrganizerRequest,
    userId: string,
  ): Promise<OrganizerApiResult<Organizer>> {
    try {
      // 1. Upload Logo if present
      let logoUrl = data.logoUrl?.trim() || data.logo_url?.trim();
      if (!logoUrl && data.logo) {
        const path = UploadService.generateAvatarPath(
          userId,
          "Organizer",
          data.logo,
        );
        const uploadResult = await UploadService.uploadFile(data.logo, path);
        if (!uploadResult.success) {
          throw new Error(uploadResult.error || "Upload failed");
        }
        logoUrl = uploadResult.url || getR2PublicUrl(uploadResult.path);
      }

      // 2. Map Payload
      const payload = {
        name: data.name,
        display_name: data.displayName,
        type: data.type,
        license_type: data.license_type,
        contact_email:
          data.contactEmail?.trim() || data.contact_email?.trim() || "",
        contact_phone:
          data.contactPhone?.trim() || data.contact_phone?.trim() || "",
        address: data.address,
        website_url: data.website?.trim() || "",
        fanpage_url: data.fanpageUrl?.trim() || "",
        contact_person: data.contactPerson?.trim() || "",
        contact_position: data.contactPosition?.trim() || "",
        logo_url: logoUrl,
      };

      // 3. Register Persona
      const { data: result } = await apiClient.post(
        "/organizer/register",
        payload,
      );
      const rawOrganizer = result.data || result;
      const organizer = organizersSchema.partial().parse(rawOrganizer);

      if (!organizer?.id) throw new Error("No Organizer ID returned");

      // 4. Upload Docs
      if (data.verificationDocuments?.length) {
        await this.uploadMultipleDocs(
          data.verificationDocuments,
          organizer.id,
          userId,
        );
      } else if (data.verificationDocument) {
        await this.uploadVerificationDocs(
          data.verificationDocument,
          organizer.id,
          userId,
        );
      }

      return {
        success: true,
        message: "Đăng ký thành công",
        data: organizer as Organizer,
      };
    } catch (error: unknown) {
      console.error("[OrganizerService] Register error:", error);
      return {
        success: false,
        message: this.getErrorMessage(error) || "Đăng ký thất bại",
        error: this.getErrorMessage(error),
      };
    }
  },

  /**
   * Upload Single Verification Document
   */
  async uploadVerificationDocs(
    file: File,
    organizerId: string,
    userId?: string,
  ): Promise<OrganizerApiResult<string>> {
    try {
      const normalizedUserId = userId?.trim() || "unknown-user";
      const safeName = file.name.replaceAll(/[^a-zA-Z0-9.-]/g, "_");
      const path = `verification/${normalizedUserId}/${organizerId}/${Date.now()}_${safeName}`;

      const uploadResult = await UploadService.uploadPrivateFile(file, path);
      if (!uploadResult.success || !uploadResult.path) {
        return {
          success: false,
          message: "Upload thất bại",
          error: uploadResult.error || "Không thể upload tài liệu xác minh",
        };
      }

      await this.updateDocsList(organizerId, [uploadResult.path]);

      return {
        success: true,
        message: "Upload thành công",
        data: uploadResult.path,
      };
    } catch (error: unknown) {
      return {
        success: false,
        message: "Upload thất bại",
        error: this.getErrorMessage(error),
      };
    }
  },

  /**
   * Upload Multiple Docs helper
   */
  async uploadMultipleDocs(
    files: File[],
    organizerId: string,
    userId?: string,
  ): Promise<string[]> {
    const paths: string[] = [];
    for (const file of files) {
      const res = await this.uploadVerificationDocs(file, organizerId, userId);
      if (res.data) paths.push(res.data);
    }
    return paths;
  },

  /**
   * Update Docs List in Organizer Profile
   */
  async updateDocsList(id: string, docs: string[]) {
    try {
      await apiClient.put(`/organizer/organizations/${id}/docs`, { docs }); // Using legacy endpoint for now
    } catch (error) {
      console.warn("Update docs list failed", error);
    }
  },

  /**
   * Update Organizer Profile
   */
  async updateOrganizer(
    id: string,
    data: Partial<RegisterOrganizerRequest>,
    userId: string,
  ): Promise<OrganizerApiResult<Organizer>> {
    try {
      let logoUrl = undefined;
      if (data.logo) {
        logoUrl = await profileService.uploadAvatar(
          data.logo,
          userId,
          "Organizer",
        );
      }

      const updateData: Record<string, unknown> = { ...data };
      if (logoUrl) updateData.logoUrl = logoUrl;
      delete updateData.logo; // Don't send file object
      delete updateData.verificationDocument;
      delete updateData.verificationDocuments;

      // Map fields if needed (camel vs snake)
      if (data.contactEmail) updateData.email = data.contactEmail;
      if (data.contactPhone) updateData.phone = data.contactPhone;

      const { data: result } = await apiClient.put(
        `/organizer/organizations/${id}`,
        updateData,
      );
      const rawData = result.data || result;

      return {
        success: true,
        message: "Cập nhật thành công",
        data: organizersSchema.partial().parse(rawData) as Organizer,
      };
    } catch (error: unknown) {
      return {
        success: false,
        message: "Cập nhật thất bại",
        error: this.getErrorMessage(error),
      };
    }
  },

  /**
   * Logo Helper
   */
  getLogoUrl(path?: string | null): string {
    if (!path) return "";
    if (path.startsWith("http")) return path;
    return getR2PublicUrl(path) || "";
  },

  /**
   * Get My Organizer Profile
   */
  async getMe(): Promise<OrganizerApiResult<Organizer>> {
    try {
      // Assuming this endpoint exists or we use profileService.getCurrentUserProfile().organization
      // But valid to have dedicated endpoint if needed.
      // For now, let's reuse /users/profile/organizer if GET exists, or derive from profile.
      const { data } = await apiClient.get("/users/profile/organizer");
      return {
        success: true,
        message: "Success",
        data: organizersSchema.partial().parse(data) as Organizer,
      };
    } catch (error: unknown) {
      return {
        success: false,
        message: "Error",
        error: this.getErrorMessage(error),
      };
    }
  },
};

export default organizerService;
